import React from 'react'
import { Helmet } from 'react-helmet-async'

interface SEOProps {
  title: string
  description: string
  type?: 'website' | 'article' | 'profile'
  image?: string
  canonicalUrl?: string
  structuredData?: object | object[]
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  type = 'website',
  image = 'https://saltymermaidart.com/social-image.jpg',
  canonicalUrl,
  structuredData
}) => {
  const siteUrl = 'https://saltymermaidart.com'
  const fullTitle = title.includes('The Salty Mermaid') ? title : `${title} | The Salty Mermaid`
  const canonical = canonicalUrl || `${siteUrl}${window.location.pathname}`

  // Default Organization schema for all pages
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'The Salty Mermaid',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: 'Art Gallery & Studio in Ormond Beach, FL offering art classes, workshops, and creative supplies',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '1546 Ocean Shore Blvd',
      addressLocality: 'Ormond Beach',
      addressRegion: 'FL',
      postalCode: '32176',
      addressCountry: 'US'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+1-904-704-7449',
      contactType: 'Customer Service',
      email: 'thesaltymermaidart@gmail.com'
    },
    sameAs: [
      'https://www.facebook.com/TheSaltyMermaidArt/'
    ]
  }

  // Combine organization schema with page-specific structured data
  const allSchemas = structuredData
    ? Array.isArray(structuredData)
      ? [organizationSchema, ...structuredData]
      : [organizationSchema, structuredData]
    : [organizationSchema]

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="The Salty Mermaid" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Structured Data (JSON-LD) */}
      {allSchemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  )
}

export default SEO
